import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { searchAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import '../styles/theme.css';
import './SearchCompanies.css';

const PROVINCES = [
  'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
  'Limpopo', 'Mpumalanga', 'North West', 'Free State', 'Northern Cape',
];

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];

const INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Education', 'Retail',
  'Manufacturing', 'Construction', 'Mining', 'Agriculture', 'Media',
  'Hospitality', 'Logistics', 'Legal', 'Consulting', 'Government',
];

export default function SearchCompanies() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('');
  const [province, setProvince] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const doSearch = useCallback(async (pageOverride = 0) => {
    setLoading(true);
    setSearched(true);
    try {
      const params = {
        skip: pageOverride * LIMIT,
        limit: LIMIT,
      };
      if (q.trim()) params.q = q.trim();
      if (industry) params.industry = industry;
      if (province) params.province = province;
      if (companySize) params.company_size = companySize;
      if (verifiedOnly) params.verified_only = true;

      const res = await searchAPI.searchCompanies(params);
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);
      setPage(pageOverride);
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, industry, province, companySize, verifiedOnly]);

  // Load initial results on mount
  useEffect(() => {
    doSearch(0);
    // eslint-disable-line
  }, []); // intentional — only run once on mount

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') doSearch(0);
  };

  const clearFilters = () => {
    setQ('');
    setIndustry('');
    setProvince('');
    setCompanySize('');
    setVerifiedOnly(false);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Find Companies</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme">
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>Find Companies</h1>
            <p>Discover employers and explore their open positions</p>
          </div>

          {/* Search bar + filters */}
          <div className="card sc-search-card">
            <div className="sc-search-row">
              <div className="sc-search-wrap">
                <i className="fas fa-search sc-search-icon"></i>
                <input
                  className="form-control sc-search-input"
                  type="text"
                  placeholder="Search by company name, industry, or city..."
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {q && (
                  <button className="sc-clear-btn" onClick={() => setQ('')} title="Clear">
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <button className="btn btn-primary sc-search-btn" onClick={() => doSearch(0)}>
                <i className="fas fa-search"></i> Search
              </button>
            </div>

            <div className="sc-filters-row">
              <select
                className="form-control sc-filter-select"
                value={industry}
                onChange={e => setIndustry(e.target.value)}
              >
                <option value="">All Industries</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>

              <select
                className="form-control sc-filter-select"
                value={province}
                onChange={e => setProvince(e.target.value)}
              >
                <option value="">All Provinces</option>
                {PROVINCES.map(p => <option key={p}>{p}</option>)}
              </select>

              <select
                className="form-control sc-filter-select"
                value={companySize}
                onChange={e => setCompanySize(e.target.value)}
              >
                <option value="">Any Size</option>
                {COMPANY_SIZES.map(s => <option key={s}>{s}</option>)}
              </select>

              <label className="sc-verified-toggle">
                <input
                  type="checkbox"
                  checked={verifiedOnly}
                  onChange={e => setVerifiedOnly(e.target.checked)}
                />
                <span>Verified only</span>
              </label>

              {(industry || province || companySize || verifiedOnly) && (
                <button className="btn btn-sm sc-clear-filters-btn" onClick={clearFilters}>
                  <i className="fas fa-times"></i> Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Searching companies...</p>
            </div>
          ) : searched && results.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-building"></i>
              <h3>No companies found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              {searched && (
                <p className="sc-results-count">
                  {total} {total === 1 ? 'company' : 'companies'} found
                </p>
              )}

              <div className="sc-grid">
                {results.map(company => (
                  <div
                    key={company.id}
                    className="sc-card card"
                    onClick={() => navigate(`/company/${encodeURIComponent(company.company_name)}`)}
                    style={company.brand_color ? { '--company-color': company.brand_color } : {}}
                  >
                    <div className="sc-card-accent" />

                    <div className="sc-card-top">
                      <div className="sc-logo-wrap">
                        {company.company_logo
                          ? <img
                              src={company.company_logo.startsWith('http')
                                ? company.company_logo
                                : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${company.company_logo.replace(/^\/+/, '')}`}
                              alt={company.company_name}
                              className="sc-logo-img"
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                          : <div className="sc-logo-placeholder">{company.company_name?.charAt(0)}</div>
                        }
                      </div>
                      <div className="sc-card-meta">
                        <div className="sc-card-name-row">
                          <h3 className="sc-company-name">{company.company_name}</h3>
                          {company.is_verified && (
                            <span className="sc-verified-badge" title="Verified employer">
                              <i className="fas fa-check-circle"></i>
                            </span>
                          )}
                        </div>
                        {company.industry && (
                          <span className="sc-industry">
                            <i className="fas fa-industry"></i> {company.industry}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="sc-card-details">
                      {(company.city || company.province) && (
                        <span>
                          <i className="fas fa-map-marker-alt"></i>
                          {[company.city, company.province].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {company.company_size && (
                        <span>
                          <i className="fas fa-users"></i> {company.company_size} employees
                        </span>
                      )}
                      <span className={`sc-jobs-count ${company.active_jobs > 0 ? 'has-jobs' : ''}`}>
                        <i className="fas fa-briefcase"></i>
                        {company.active_jobs > 0
                          ? `${company.active_jobs} open position${company.active_jobs !== 1 ? 's' : ''}`
                          : 'No open positions'}
                      </span>
                    </div>

                    {company.tagline && (
                      <p className="sc-tagline">"{company.tagline}"</p>
                    )}

                    {company.benefits?.length > 0 && (
                      <div className="sc-benefits">
                        {company.benefits.slice(0, 3).map((b, i) => (
                          <span key={i} className="sc-benefit-tag">
                            <i className="fas fa-check"></i> {b}
                          </span>
                        ))}
                        {company.benefits.length > 3 && (
                          <span className="sc-benefit-tag sc-benefit-more">
                            +{company.benefits.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    <div className="sc-card-footer">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/company/${encodeURIComponent(company.company_name)}`);
                        }}
                      >
                        View Profile <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="sc-pagination">
                  <button
                    className="btn btn-sm"
                    disabled={page === 0}
                    onClick={() => doSearch(page - 1)}
                  >
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  <span className="sc-page-info">Page {page + 1} of {totalPages}</span>
                  <button
                    className="btn btn-sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => doSearch(page + 1)}
                  >
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
